import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, 
  Store, 
  Users, 
  TrendingUp, 
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Edit,
  Trash2,
  Search,
  IndianRupee,
  DollarSign,
  ShoppingBag,
  Star,
  Activity,
  Clock,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Package,
  UserCheck,
  ChefHat,
  MapPin,
  Calendar,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  Eye,
  Globe,
  Zap,
  Target,
  Award,
  Percent,
  Bell,
  Shield,
  Database,
  Mail,
  Lock,
  Smartphone,
  CreditCard,
  FileText,
  Save,
  RefreshCw
} from 'lucide-react';
import type { User } from '../../App';
import { api } from '../../lib/api';
import { resolveAssetUrl } from '../../lib/assets';
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { AddUserModal } from '../modals/AddUserModal';
import { AddRestaurantModal } from '../modals/AddRestaurantModal';
import { MasterDataManager } from '../masterdata/MasterDataManager';
import { ToastContainer, useToast } from '../ui/Toast';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface SuperAdminDashboardProps {
  user: User;
  onLogout: () => void;
}

type Tab = 'dashboard' | 'restaurants' | 'users' | 'analytics' | 'deliveryApplications' | 'masterdata' | 'settings';
type SettingsSection = 'general' | 'notifications' | 'security' | 'data_privacy' | 'email_templates' | 'payment_methods';
type AnalyticsRange = '7d' | '30d' | '90d' | '1y';
type DeliveryApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL';

type DeliveryApplicationRecord = {
  id: string;
  name: string;
  email: string;
  phone: string;
  vehicleType: string;
  vehicleNumber?: string | null;
  licenseNumber?: string | null;
  drivingLicenseUrl?: string | null;
  drivingLicenseName?: string | null;
  deliveryArea?: string | null;
  idProofUrl?: string | null;
  idProofName?: string | null;
  bankAccountName?: string | null;
  bankName?: string | null;
  branchName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNotes?: string | null;
  reviewedBy?: string | null;
  accountUserId?: string | null;
  reviewedAt?: string | null;
  activatedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const DEFAULT_RESTAURANT_BANNER = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=600&fit=crop';
const DEFAULT_RESTAURANT_LOGO = 'https://api.dicebear.com/7.x/initials/svg?seed=Restaurant';

const normalizeRestaurant = (restaurant: any) => {
  const cuisineTypeId =
    restaurant?.cuisineTypeId ||
    restaurant?.cuisine_type_id ||
    restaurant?.cuisineType?.id ||
    restaurant?.cuisine_type?.id ||
    null;
  const cuisineTypeName =
    restaurant?.cuisineType?.name ||
    restaurant?.cuisine_type?.name ||
    restaurant?.cuisineTypeName ||
    restaurant?.cuisine_type_name ||
    '';

  const cuisine = Array.isArray(restaurant?.cuisine)
    ? restaurant.cuisine
    : typeof restaurant?.cuisine === 'string'
      ? restaurant.cuisine.split(',').map((c: string) => c.trim()).filter(Boolean)
      : cuisineTypeName
        ? [cuisineTypeName]
        : cuisineTypeId
          ? [String(cuisineTypeId)]
          : [];

  const bannerRaw = restaurant?.banner || restaurant?.cover_image || restaurant?.coverImage || restaurant?.image || '';
  const banner = resolveAssetUrl(bannerRaw) || DEFAULT_RESTAURANT_BANNER;
  const logo = resolveAssetUrl(restaurant?.logo) || DEFAULT_RESTAURANT_LOGO;
  const currency =
    restaurant?.currency ||
    restaurant?.currencyMaster ||
    restaurant?.currencyRef ||
    null;
  const currencyId =
    restaurant?.currencyId ||
    restaurant?.currency_id ||
    currency?.id ||
    null;

  return {
    ...restaurant,
    description: restaurant?.description || '',
    logo,
    banner,
    image: banner,
    city: restaurant?.city || '-',
    address: restaurant?.address || '-',
    rating: Number(restaurant?.rating) || 0,
    totalOrders: Number(restaurant?.total_orders ?? restaurant?.totalOrders ?? 0),
    totalRatings: Number(restaurant?.total_ratings ?? restaurant?.totalRatings ?? 0),
    deliveryTime: restaurant?.delivery_time || restaurant?.deliveryTime || '-',
    isActive: Boolean(restaurant?.is_active ?? restaurant?.isActive),
    pincode: restaurant?.pincode || restaurant?.zipCode || '',
    currencyId,
    currency,
    cuisine,
    cuisineTypeId: cuisineTypeId ? String(cuisineTypeId) : null,
    cuisineTypeName: String(cuisineTypeName || '').trim(),
  };
};

const buildSuperAdminSettingsStorageKey = (user: User) =>
  `superadmin.settings.${user?.id || user?.email || 'default'}`;

const defaultNotificationPreferences = [
  { key: 'email_orders', label: 'Email notifications for new orders', checked: true },
  { key: 'sms_critical', label: 'SMS alerts for critical issues', checked: true },
  { key: 'push_restaurant_updates', label: 'Push notifications for restaurant updates', checked: false },
  { key: 'weekly_reports', label: 'Weekly performance reports', checked: true },
  { key: 'feedback_alerts', label: 'Customer feedback alerts', checked: true },
];

type NotificationLevel = 'info' | 'warning' | 'success';
type AdminNotification = {
  id: string;
  title: string;
  message: string;
  source: string;
  level: NotificationLevel;
  createdAt: number;
  read: boolean;
};

type PrivacyControl = {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
};

type EmailTemplateConfig = {
  key: string;
  name: string;
  channel: 'email' | 'sms' | 'push';
  enabled: boolean;
  deliveries: number;
  lastTriggeredAt: number;
};

type PaymentMethodConfig = {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  usageCount: number;
  lastUsedAt: number | null;
};

type RecentActivity = {
  id: string;
  ts: number;
  message: string;
  icon: typeof ShoppingBag;
  color: string;
};

type CityAnalytics = {
  city: string;
  revenue: number;
  orders: number;
};

const defaultPrivacyControls: PrivacyControl[] = [
  {
    key: 'collect_analytics',
    label: 'Analytics Collection',
    description: 'Track usage events for platform optimization',
    enabled: true,
  },
  {
    key: 'store_activity_logs',
    label: 'Activity Logs',
    description: 'Keep admin and user activity trail for auditing',
    enabled: true,
  },
  {
    key: 'share_anonymized_data',
    label: 'Share Anonymized Data',
    description: 'Use masked data for business intelligence reports',
    enabled: false,
  },
];

const defaultEmailTemplates: EmailTemplateConfig[] = [
  { key: 'welcome_user', name: 'Welcome User', channel: 'email', enabled: true, deliveries: 0, lastTriggeredAt: Date.now() },
  { key: 'order_alert', name: 'Order Alert', channel: 'email', enabled: true, deliveries: 0, lastTriggeredAt: Date.now() },
  { key: 'critical_incident', name: 'Critical Incident', channel: 'sms', enabled: true, deliveries: 0, lastTriggeredAt: Date.now() },
  { key: 'weekly_digest', name: 'Weekly Digest', channel: 'push', enabled: true, deliveries: 0, lastTriggeredAt: Date.now() },
];

const formatRelativeTime = (timestamp: number) => {
  const diff = Math.max(0, Date.now() - timestamp);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const normalizeOrderStatus = (status: unknown): string => {
  const normalized = String(status || '').trim().toUpperCase();
  switch (normalized) {
    case 'PENDING':
      return 'pending';
    case 'CONFIRMED':
    case 'ACCEPTED':
      return 'confirmed';
    case 'PREPARING':
      return 'preparing';
    case 'READY':
      return 'ready';
    case 'OUT_FOR_DELIVERY':
      return 'out_for_delivery';
    case 'DELIVERED':
    case 'COMPLETED':
      return 'delivered';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'pending';
  }
};

const normalizeOrder = (order: any) => ({
  ...order,
  total: Number(order?.total ?? 0),
  status: normalizeOrderStatus(order?.status),
  createdAt: order?.createdAt || order?.placedAt || new Date().toISOString(),
  restaurantName: order?.restaurant?.name || order?.restaurantName || 'Restaurant',
});

export function SuperAdminDashboard({ user, onLogout }: SuperAdminDashboardProps) {
  const USERS_SUMMARY_LIMIT = 100; // backend validatePaginationQuery enforces limit <= 100
  const settingsStorageKey = buildSuperAdminSettingsStorageKey(user);
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month' | 'year'>('today');
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalMode, setUserModalMode] = useState<'add' | 'edit'>('add');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showAddRestaurantModal, setShowAddRestaurantModal] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersActionLoadingId, setUsersActionLoadingId] = useState<string | null>(null);
  const { toasts, removeToast, success, error } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [usersTable, setUsersTable] = useState<any[]>([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersLimit, setUsersLimit] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersCountTotal, setUsersCountTotal] = useState<number | null>(null);
  const [restaurantSearchTerm, setRestaurantSearchTerm] = useState('');
  const [restaurantStatusFilter, setRestaurantStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [restaurantCityFilter, setRestaurantCityFilter] = useState('all');
  const [restaurantCuisineFilter, setRestaurantCuisineFilter] = useState('all');
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [restaurantsActionLoading, setRestaurantsActionLoading] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    platformName: 'FoodHub',
    supportEmail: 'support@foodhub.com',
    timezone: '',
    currency: ''
  });
  const [timezoneOptions, setTimezoneOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [currencyOptions, setCurrencyOptions] = useState<Array<{ id: string; code: string; symbol: string }>>([]);
  const [notificationPreferences, setNotificationPreferences] = useState(defaultNotificationPreferences);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState('30 minutes');
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('general');
  const [notificationFeed, setNotificationFeed] = useState<AdminNotification[]>([]);
  const [notificationRefreshing, setNotificationRefreshing] = useState(false);
  const [lastNotificationSync, setLastNotificationSync] = useState<number | null>(null);
  const [privacyControls, setPrivacyControls] = useState<PrivacyControl[]>(defaultPrivacyControls);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateConfig[]>(defaultEmailTemplates);
  const [paymentMethodsConfig, setPaymentMethodsConfig] = useState<PaymentMethodConfig[]>([]);
  const [securityEvents, setSecurityEvents] = useState<Array<{ id: string; title: string; status: 'ok' | 'warn'; createdAt: number }>>([]);
  const [lastRealtimeSettingsSync, setLastRealtimeSettingsSync] = useState<number | null>(null);
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>('30d');

  const [restaurantsList, setRestaurantsList] = useState<any[]>([]);
  const [cuisineTypeNameMap, setCuisineTypeNameMap] = useState<Record<string, string>>({});
  const [orders, setOrders] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [deliveryApplications, setDeliveryApplications] = useState<DeliveryApplicationRecord[]>([]);
  const [deliveryApplicationsLoading, setDeliveryApplicationsLoading] = useState(false);
  const [deliveryApplicationsSearch, setDeliveryApplicationsSearch] = useState('');
  const [deliveryApplicationsStatus, setDeliveryApplicationsStatus] = useState<DeliveryApplicationStatus>('ALL');
  const [selectedDeliveryApplication, setSelectedDeliveryApplication] = useState<DeliveryApplicationRecord | null>(null);
  const [showDeliveryApplicationModal, setShowDeliveryApplicationModal] = useState(false);
  const [deliveryReviewNotes, setDeliveryReviewNotes] = useState('');
  const [deliveryReviewActionLoading, setDeliveryReviewActionLoading] = useState(false);

  const isNotificationEnabled = (key: string) =>
    notificationPreferences.find((item) => item.key === key)?.checked ?? false;

  const mergeReadState = (nextFeed: AdminNotification[], previousFeed: AdminNotification[]) => {
    const previousReadMap = new Map(previousFeed.map((item) => [item.id, item.read]));
    return nextFeed.map((item) => ({
      ...item,
      read: previousReadMap.get(item.id) ?? item.read,
    }));
  };

  const buildRealtimeNotifications = (nextUsers: any[], nextRestaurants: any[]): AdminNotification[] => {
    const now = Date.now();
    const notifications: AdminNotification[] = [];

    const inactiveRestaurants = nextRestaurants.filter((r) => !r?.isActive).length;
    const lowRatedRestaurants = nextRestaurants.filter((r) => Number(r?.rating || 0) > 0 && Number(r?.rating || 0) <= 3).length;
    const disabledUsers = nextUsers.filter((u) => u?.isActive === false).length;
    const recentUsers = nextUsers.filter((u) => {
      if (!u?.createdAt) return false;
      return now - new Date(u.createdAt).getTime() <= 24 * 60 * 60 * 1000;
    }).length;
    const pendingOrders = orders.filter((o: any) => String(o?.status || '').toLowerCase() === 'pending').length;

    if (isNotificationEnabled('email_orders')) {
      notifications.push({
        id: `orders-pending-${pendingOrders}`,
        title: 'Order Queue Update',
        message: `${pendingOrders} pending order${pendingOrders === 1 ? '' : 's'} in the system.`,
        source: 'Orders',
        level: pendingOrders > 20 ? 'warning' : 'info',
        createdAt: now - 2 * 60 * 1000,
        read: false,
      });
    }

    if (isNotificationEnabled('push_restaurant_updates')) {
      notifications.push({
        id: `restaurants-total-${nextRestaurants.length}`,
        title: 'Restaurant Network Status',
        message: `${nextRestaurants.length} total restaurants, ${inactiveRestaurants} inactive currently.`,
        source: 'Restaurants',
        level: inactiveRestaurants > 0 ? 'warning' : 'success',
        createdAt: now - 4 * 60 * 1000,
        read: false,
      });
    }

    if (isNotificationEnabled('feedback_alerts')) {
      notifications.push({
        id: `ratings-low-${lowRatedRestaurants}`,
        title: 'Feedback Alerts',
        message: `${lowRatedRestaurants} restaurant${lowRatedRestaurants === 1 ? '' : 's'} with low ratings need attention.`,
        source: 'Ratings',
        level: lowRatedRestaurants > 0 ? 'warning' : 'success',
        createdAt: now - 8 * 60 * 1000,
        read: false,
      });
    }

    if (isNotificationEnabled('weekly_reports')) {
      notifications.push({
        id: `users-recent-${recentUsers}`,
        title: 'Growth Snapshot',
        message: `${recentUsers} new user${recentUsers === 1 ? '' : 's'} in the last 24 hours.`,
        source: 'Users',
        level: 'info',
        createdAt: now - 12 * 60 * 1000,
        read: false,
      });
    }

    if (isNotificationEnabled('sms_critical') && (disabledUsers > 0 || inactiveRestaurants > 0)) {
      notifications.push({
        id: `critical-${disabledUsers}-${inactiveRestaurants}`,
        title: 'Critical Operational Alert',
        message: `${disabledUsers} disabled users and ${inactiveRestaurants} inactive restaurants detected.`,
        source: 'Security',
        level: 'warning',
        createdAt: now - 1 * 60 * 1000,
        read: false,
      });
    }

    return notifications.sort((a, b) => b.createdAt - a.createdAt);
  };

  const buildSecurityEvents = (nextUsers: any[], nextRestaurants: any[]) => {
    const now = Date.now();
    const disabledUsers = nextUsers.filter((u) => u?.isActive === false).length;
    const inactiveRestaurants = nextRestaurants.filter((r) => !r?.isActive).length;
    const activeUsers = nextUsers.length - disabledUsers;
    return [
      {
        id: `sec-active-users-${activeUsers}`,
        title: `${activeUsers} active user accounts monitored`,
        status: 'ok' as const,
        createdAt: now - 2 * 60 * 1000,
      },
      {
        id: `sec-disabled-users-${disabledUsers}`,
        title: `${disabledUsers} disabled user accounts detected`,
        status: disabledUsers > 0 ? ('warn' as const) : ('ok' as const),
        createdAt: now - 6 * 60 * 1000,
      },
      {
        id: `sec-inactive-restaurants-${inactiveRestaurants}`,
        title: `${inactiveRestaurants} inactive restaurants affecting coverage`,
        status: inactiveRestaurants > 0 ? ('warn' as const) : ('ok' as const),
        createdAt: now - 9 * 60 * 1000,
      },
    ];
  };

  const hydrateEmailTemplateMetrics = (templates: EmailTemplateConfig[]) => {
    const now = Date.now();
    const pendingOrders = orders.filter((o: any) => String(o?.status || '').toLowerCase() === 'pending').length;
    return templates.map((template, idx) => ({
      ...template,
      deliveries:
        template.key === 'order_alert'
          ? pendingOrders
          : template.key === 'welcome_user'
            ? users.length
            : template.deliveries,
      lastTriggeredAt: now - (idx + 1) * 5 * 60 * 1000,
    }));
  };

  const refreshRealtimeNotifications = async () => {
    try {
      setNotificationRefreshing(true);
      const [usersResponse, restaurantsResponse] = await Promise.all([
        api.users.getAll({ page: 1, limit: 100 } as any),
        api.restaurants.getAll({ limit: 100, status: 'all' } as any),
      ]);

      const nextUsers = usersResponse?.data?.users || [];
      const usersPagination = (usersResponse as any)?.data?.pagination || {};
      const resolvedUsersTotal =
        Number(usersPagination?.total ?? usersPagination?.totalItems ?? usersPagination?.count ?? 0) ||
        Number(nextUsers.length || 0);
      const nextRestaurants = Array.isArray(restaurantsResponse?.data)
        ? restaurantsResponse.data.map(normalizeRestaurant)
        : Array.isArray((restaurantsResponse as any)?.data?.restaurants)
          ? (restaurantsResponse as any).data.restaurants.map(normalizeRestaurant)
          : [];

      setUsers(nextUsers);
      setUsersCountTotal(resolvedUsersTotal);
      setRestaurantsList(nextRestaurants);

      const generated = buildRealtimeNotifications(nextUsers, nextRestaurants);
      setNotificationFeed((prev) => mergeReadState(generated, prev));
      setLastNotificationSync(Date.now());
    } catch (refreshError: any) {
      console.error('Failed to refresh realtime notifications:', refreshError);
      const fallback: AdminNotification = {
        id: `notify-error-${Date.now()}`,
        title: 'Live Notification Refresh Failed',
        message: String(refreshError?.message || 'Unable to refresh notification center'),
        source: 'System',
        level: 'warning',
        createdAt: Date.now(),
        read: false,
      };
      setNotificationFeed((prev) => [fallback, ...prev].slice(0, 15));
    } finally {
      setNotificationRefreshing(false);
    }
  };

  const refreshAdvancedSettingsData = async () => {
    try {
      const response = await api.masterData.paymentMethods.getAll();
      const rows = Array.isArray(response?.data) ? response.data : [];
      const now = Date.now();

      const mapped: PaymentMethodConfig[] = rows.map((row: any) => {
        const code = String(row?.code || row?.name || '').trim();
        const usageCount = orders.filter((order: any) => {
          const paymentValue = String(order?.paymentMethod || order?.payment_method || '').trim().toLowerCase();
          return paymentValue === code.toLowerCase() || paymentValue === String(row?.name || '').trim().toLowerCase();
        }).length;

        return {
          id: String(row?.id || code || Math.random()),
          code,
          name: String(row?.name || row?.code || 'Payment Method'),
          isActive: row?.isActive !== false,
          usageCount,
          lastUsedAt: usageCount > 0 ? now - Math.max(1, usageCount) * 60 * 1000 : null,
        };
      });

      setPaymentMethodsConfig(mapped);
      setLastRealtimeSettingsSync(Date.now());
    } catch (advancedError) {
      console.error('Failed to refresh advanced settings data:', advancedError);
      setLastRealtimeSettingsSync(Date.now());
    }
  };

  const loadUsersSummary = async () => {
    try {
      const response = await api.users.getAll({ page: 1, limit: 200 } as any);
      const nextUsers = response.data?.users || [];
      const pagination = (response as any)?.data?.pagination || {};
      const resolvedUsersTotal =
        Number(pagination?.total ?? pagination?.totalItems ?? pagination?.count ?? 0) ||
        Number(nextUsers.length || 0);
      setUsers(nextUsers);
      setUsersCountTotal(resolvedUsersTotal);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      const message = String(error?.message || '');
      if (message.includes('[401]') && message.toLowerCase().includes('token expired')) {
        onLogout();
      }
    }
  };

  const loadUsersTable = async (overrides: { page?: number; limit?: number } = {}) => {
    const page = overrides.page ?? usersPage;
    const limit = overrides.limit ?? usersLimit;
    const search = searchTerm ? searchTerm.trim() : '';

    try {
      setUsersLoading(true);
      const response = await api.users.getAll({
        page,
        limit,
        ...(search ? { search } : {}),
      } as any);
      const nextUsers = response.data?.users || [];
      const pagination = (response as any)?.data?.pagination || {};
      const resolvedTotal =
        Number(pagination?.total ?? pagination?.totalItems ?? pagination?.count ?? 0) ||
        Number(nextUsers.length || 0);
      const resolvedTotalPages =
        Number(pagination?.totalPages ?? pagination?.pages ?? 0) ||
        Math.max(1, Math.ceil(resolvedTotal / limit));

      setUsersTable(nextUsers);
      setUsersTotal(resolvedTotal);
      setUsersCountTotal(resolvedTotal);
      setUsersTotalPages(resolvedTotalPages);

      if (nextUsers.length === 0 && page > 1) {
        setUsersPage(page - 1);
        return;
      }

      if (page !== usersPage) setUsersPage(page);
      if (limit !== usersLimit) setUsersLimit(limit);
    } catch (error: any) {
      console.error('Failed to load users table:', error);
      const message = String(error?.message || '');
      if (message.includes('[401]') && message.toLowerCase().includes('token expired')) {
        onLogout();
        return;
      }
      setUsersTable([]);
      setUsersTotal(0);
      setUsersCountTotal(0);
      setUsersTotalPages(1);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadRestaurants = async () => {
    try {
      setRestaurantsLoading(true);
      const response = await api.restaurants.getAll({ limit: 200, status: 'all' } as any);
      const restaurantData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray((response as any)?.data?.restaurants)
          ? (response as any).data.restaurants
          : [];
      setRestaurantsList(restaurantData.map(normalizeRestaurant));
    } catch (loadError: any) {
      console.error('Failed to load restaurants:', loadError);
      error(loadError?.message || 'Failed to load restaurants');
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const limit = 200;
      let page = 1;
      let totalPages = 1;
      const collected: any[] = [];

      while (page <= totalPages) {
        const response: any = await api.orders.getAll({ page: page as any, limit: limit as any } as any);
        const payload = response?.data;
        const pageOrders = Array.isArray(payload?.orders)
          ? payload.orders
          : Array.isArray(payload)
            ? payload
            : [];
        collected.push(...pageOrders);

        const paginationTotalPages = Number(payload?.pagination?.totalPages || 0);
        if (paginationTotalPages > 0) {
          totalPages = paginationTotalPages;
        } else {
          totalPages = pageOrders.length >= limit ? page + 1 : page;
        }
        page += 1;
      }

      setOrders(collected.map(normalizeOrder));
    } catch (err) {
      console.error('Failed to load orders:', err);
      setOrders([]);
    }
  };

  const loadCuisineTypes = async () => {
    try {
      const response = await api.masterData.cuisineTypes.getAll();
      const items = Array.isArray(response?.data)
        ? response.data
        : Array.isArray((response as any)?.data?.data)
          ? (response as any).data.data
          : Array.isArray((response as any)?.data?.items)
            ? (response as any).data.items
            : [];
      const nextMap = items.reduce((acc: Record<string, string>, item: any) => {
        const id = String(item?.id ?? '').trim();
        const name = String(item?.name ?? '').trim();
        if (id) acc[id] = name || id;
        return acc;
      }, {});
      setCuisineTypeNameMap(nextMap);
    } catch (err) {
      console.error('Failed to load cuisine types:', err);
    }
  };

  const loadDeliveryApplications = async () => {
    try {
      setDeliveryApplicationsLoading(true);
      const response = await api.deliveryApplications.list({
        page: 1,
        limit: 100,
        search: deliveryApplicationsSearch || undefined,
        status: deliveryApplicationsStatus === 'ALL' ? undefined : deliveryApplicationsStatus,
      });
      setDeliveryApplications(Array.isArray(response?.data?.applications) ? response.data.applications : []);
    } catch (loadError: any) {
      console.error('Failed to load delivery applications:', loadError);
      error(loadError?.message || 'Failed to load delivery applications');
      setDeliveryApplications([]);
    } finally {
      setDeliveryApplicationsLoading(false);
    }
  };

  const openDeliveryApplicationModal = (application: DeliveryApplicationRecord) => {
    setSelectedDeliveryApplication(application);
    setDeliveryReviewNotes(application.reviewNotes || '');
    setShowDeliveryApplicationModal(true);
  };

  const handleDeliveryApplicationReview = async (status: 'APPROVED' | 'REJECTED') => {
    if (!selectedDeliveryApplication) return;

    try {
      setDeliveryReviewActionLoading(true);
      const response = await api.deliveryApplications.review(selectedDeliveryApplication.id, {
        status,
        reviewNotes: deliveryReviewNotes || undefined,
      });
      success(response?.message || `Application ${status.toLowerCase()} successfully`);
      setShowDeliveryApplicationModal(false);
      setSelectedDeliveryApplication(null);
      setDeliveryReviewNotes('');
      await loadDeliveryApplications();
    } catch (reviewError: any) {
      error(reviewError?.message || 'Failed to review application');
    } finally {
      setDeliveryReviewActionLoading(false);
    }
  };

  const loadSavedSettings = () => {
    try {
      const raw = localStorage.getItem(settingsStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.settings) {
        setSettings((prev) => ({ ...prev, ...parsed.settings }));
      }
      if (Array.isArray(parsed?.notificationPreferences)) {
        setNotificationPreferences(parsed.notificationPreferences);
      }
      if (typeof parsed?.twoFactorEnabled === 'boolean') {
        setTwoFactorEnabled(parsed.twoFactorEnabled);
      }
      if (typeof parsed?.sessionTimeout === 'string') {
        setSessionTimeout(parsed.sessionTimeout);
      }
      if (Array.isArray(parsed?.privacyControls)) {
        setPrivacyControls(parsed.privacyControls);
      }
      if (Array.isArray(parsed?.emailTemplates)) {
        setEmailTemplates(parsed.emailTemplates);
      }
      if (Array.isArray(parsed?.paymentMethodsConfig)) {
        setPaymentMethodsConfig(parsed.paymentMethodsConfig);
      }
      setSettingsDirty(false);
    } catch (err) {
      console.error('Failed to parse saved super admin settings:', err);
    }
  };

  const loadSettingsMasterData = async () => {
    try {
      const [timezonesResponse, currenciesResponse] = await Promise.all([
        api.masterData.timezones.getAll(),
        api.masterData.currencies.getAll(),
      ]);

      const timezones = (Array.isArray(timezonesResponse?.data) ? timezonesResponse.data : [])
        .filter((row: any) => row?.isActive !== false)
        .map((row: any) => ({ id: String(row.id), name: String(row.name || row.code || row.offset || '') }))
        .filter((row: any) => row.name);
      const currencies = (Array.isArray(currenciesResponse?.data) ? currenciesResponse.data : [])
        .filter((row: any) => row?.isActive !== false)
        .map((row: any) => ({
          id: String(row.id),
          code: String(row.code || ''),
          symbol: String(row.symbol || row?.metadata?.symbol || ''),
        }))
        .filter((row: any) => row.code);

      setTimezoneOptions(timezones);
      setCurrencyOptions(currencies);
      setSettings((prev) => ({
        ...prev,
        timezone: prev.timezone || timezones[0]?.name || '',
        currency: prev.currency || (currencies[0] ? `${currencies[0].code} (${currencies[0].symbol || currencies[0].code})` : ''),
      }));
    } catch (err) {
      console.error('Failed to load settings master data:', err);
    }
  };

  const saveSettingsToStorage = () => {
    localStorage.setItem(
      settingsStorageKey,
      JSON.stringify({
        settings,
        notificationPreferences,
        twoFactorEnabled,
        sessionTimeout,
        privacyControls,
        emailTemplates,
        paymentMethodsConfig,
      })
    );
    setSettingsDirty(false);
    success('Settings saved successfully');
  };

  const resolveCuisineName = (value: string) => {
    if (!value) return value;
    const mapped = cuisineTypeNameMap[value];
    if (mapped) return mapped;
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    );
    return looksLikeUuid ? 'Unknown Cuisine' : value;
  };

  useEffect(() => {
    loadSavedSettings();
    void loadUsersSummary();
    void loadRestaurants();
    void loadOrders();
    void loadDeliveryApplications();
    void loadCuisineTypes();
    void loadSettingsMasterData();
    void refreshRealtimeNotifications();
    void refreshAdvancedSettingsData();
  }, []);

  useEffect(() => {
    if (activeTab !== 'users') return;
    void loadUsersTable();
  }, [activeTab, usersPage]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    if (usersPage !== 1) {
      setUsersPage(1);
      return;
    }
    void loadUsersTable({ page: 1 });
  }, [activeTab, usersLimit, searchTerm]);

  useEffect(() => {
    void loadDeliveryApplications();
  }, [deliveryApplicationsSearch, deliveryApplicationsStatus]);

  useEffect(() => {
    const timer = setInterval(() => {
      void refreshRealtimeNotifications();
      void refreshAdvancedSettingsData();
      void loadOrders();
    }, 30000);
    return () => clearInterval(timer);
  }, [notificationPreferences]);

  useEffect(() => {
    setSecurityEvents(buildSecurityEvents(users, restaurantsList));
    setEmailTemplates((prev) => hydrateEmailTemplateMetrics(prev));
  }, [users, restaurantsList]);

  const markAllNotificationsAsRead = () => {
    setNotificationFeed((prev) => prev.map((item) => ({ ...item, read: true })));
  };

  const clearReadNotifications = () => {
    setNotificationFeed((prev) => prev.filter((item) => !item.read));
  };

  const toggleNotificationRead = (notificationId: string) => {
    setNotificationFeed((prev) =>
      prev.map((item) =>
        item.id === notificationId ? { ...item, read: !item.read } : item
      )
    );
  };

  const togglePrivacyControl = (key: string) => {
    setPrivacyControls((prev) =>
      prev.map((item) => (item.key === key ? { ...item, enabled: !item.enabled } : item))
    );
    setSettingsDirty(true);
  };

  const toggleEmailTemplate = (key: string) => {
    setEmailTemplates((prev) =>
      prev.map((item) => (item.key === key ? { ...item, enabled: !item.enabled } : item))
    );
    setSettingsDirty(true);
  };

  const togglePaymentMethodConfig = (id: string) => {
    setPaymentMethodsConfig((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isActive: !item.isActive } : item))
    );
    setSettingsDirty(true);
  };

  const handleUserSaved = (message?: string, newUser?: any) => {
    success(message || (userModalMode === 'edit' ? 'User updated successfully!' : 'User created successfully!'));
    if (newUser) {
      setUsers((prev) => [newUser, ...prev.filter((u) => u.id !== newUser.id)]);
    }
    setShowUserModal(false);
    setSelectedUser(null);
    setUserModalMode('add');
    void loadUsersSummary();
    if (activeTab === 'users') {
      setUsersPage(1);
      void loadUsersTable({ page: 1 });
    }
  };

  const handleOpenAddUserModal = () => {
    setSelectedUser(null);
    setUserModalMode('add');
    setShowUserModal(true);
  };

  const handleEditUser = (targetUser: any) => {
    setSelectedUser(targetUser);
    setUserModalMode('edit');
    setShowUserModal(true);
  };

  const handleDeleteUser = async (targetUser: any) => {
    const confirmed = window.confirm(`Delete user ${targetUser.name}?`);
    if (!confirmed) return;

    try {
      setUsersActionLoadingId(targetUser.id);
      await api.users.delete(targetUser.id);
      success('User deleted successfully');
      void loadUsersSummary();
      if (activeTab === 'users') {
        void loadUsersTable();
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to delete user');
    } finally {
      setUsersActionLoadingId(null);
    }
  };

  const handleRestaurantAdded = (message?: string, newRestaurant?: any) => {
    success(message || 'Restaurant created successfully!');
    void loadRestaurants();
  };

  const handleViewRestaurant = (restaurant: any) => {
    setSelectedRestaurant(restaurant);
    setShowViewModal(true);
  };

  const handleEditRestaurant = async (restaurant: any) => {
    try {
      const id = String(restaurant?.id || '').trim();
      if (!id) {
        setSelectedRestaurant(restaurant);
        setShowEditModal(true);
        return;
      }
      const response = await api.restaurants.getById(id);
      const fullRestaurant = response?.data ? normalizeRestaurant(response.data) : restaurant;
      setSelectedRestaurant(fullRestaurant);
      setShowEditModal(true);
    } catch (err) {
      setSelectedRestaurant(restaurant);
      setShowEditModal(true);
    }
  };

  const handleDeleteRestaurant = (restaurant: any) => {
    setSelectedRestaurant(restaurant);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteRestaurant = async () => {
    if (!selectedRestaurant) return;
    try {
      setRestaurantsActionLoading(true);
      await api.restaurants.delete(selectedRestaurant.id);
      success('Restaurant deleted successfully!');
      setShowDeleteConfirm(false);
      setSelectedRestaurant(null);
      await loadRestaurants();
    } catch (deleteError: any) {
      console.error('Failed to delete restaurant:', deleteError);
      error(deleteError?.message || 'Failed to delete restaurant');
    } finally {
      setRestaurantsActionLoading(false);
    }
  };

  const handleRestaurantUpdate = async (payload: any) => {
    if (!selectedRestaurant) return;

    try {
      setRestaurantsActionLoading(true);
      await api.restaurants.update(selectedRestaurant.id, payload);
      success('Restaurant updated successfully!');
      setShowEditModal(false);
      setSelectedRestaurant(null);
      await loadRestaurants();
    } catch (updateError: any) {
      console.error('Failed to update restaurant:', updateError);
      error(updateError?.message || 'Failed to update restaurant');
    } finally {
      setRestaurantsActionLoading(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = usersTable.filter((u) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      u.name.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search) ||
      u.role.toLowerCase().includes(search) ||
      (u.phone && u.phone.includes(search))
    );
  });

  const usersRangeStart = usersTotal ? (usersPage - 1) * usersLimit + 1 : 0;
  const usersRangeEnd = usersTotal ? Math.min(usersTotal, usersPage * usersLimit) : 0;

  const filteredRestaurants = restaurantsList
    .filter((r) => {
      if (!restaurantSearchTerm) return true;
      const search = restaurantSearchTerm.toLowerCase();
      const cuisineValues = [
        ...(Array.isArray(r.cuisine) ? r.cuisine : []),
        r?.cuisineTypeId,
        r?.cuisineTypeName,
        r?.cuisineType?.id,
        r?.cuisineType?.name,
      ];
      const cuisineSearchText = [
        ...cuisineValues.flatMap((value: any) => {
          if (!value) return [];
          if (typeof value === 'string' || typeof value === 'number') return [String(value)];
          if (typeof value === 'object') {
            return [value?.id, value?.name, value?.code].map((v: any) => String(v || ''));
          }
          return [String(value)];
        }).map((v: string) => v.trim()),
        ...cuisineValues.flatMap((value: any) => {
          if (!value) return [];
          if (typeof value === 'string' || typeof value === 'number') return [resolveCuisineName(String(value).trim())];
          if (typeof value === 'object') {
            const id = String(value?.id || '').trim();
            const name = String(value?.name || '').trim();
            return [name, id ? resolveCuisineName(id) : ''].filter(Boolean);
          }
          return [];
        }).map((v: string) => v.trim()),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return (
        String(r.name || '').toLowerCase().includes(search) ||
        String(r.description || '').toLowerCase().includes(search) ||
        String(r.city || '').toLowerCase().includes(search) ||
        String(r.address || '').toLowerCase().includes(search) ||
        cuisineSearchText.includes(search)
      );
    })
    .filter((r) => {
      if (restaurantStatusFilter === 'all') return true;
      return restaurantStatusFilter === 'active' ? Boolean(r.isActive) : !Boolean(r.isActive);
    })
    .filter((r) => {
      if (restaurantCityFilter === 'all') return true;
      return String(r.city || '').trim() === restaurantCityFilter;
    })
    .filter((r) => {
      if (restaurantCuisineFilter === 'all') return true;
      const cuisineTypeId = String(r?.cuisineTypeId || r?.cuisineType?.id || '').trim();
      if (cuisineTypeId && cuisineTypeId === restaurantCuisineFilter) return true;
      return Array.isArray(r.cuisine)
        ? r.cuisine.some((value: any) => String(value || '').trim() === restaurantCuisineFilter)
        : false;
    });

  const restaurantCityOptions = Array.from(
    new Set(
      restaurantsList
        .map((r) => String(r?.city || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  const restaurantCuisineOptions =
    Object.keys(cuisineTypeNameMap).length > 0
      ? Object.entries(cuisineTypeNameMap)
          .map(([id, name]) => ({ id: String(id || '').trim(), name: String(name || '').trim() }))
          .filter((item) => item.id)
          .sort((a, b) => a.name.localeCompare(b.name))
      : Array.from(
          new Set(
            restaurantsList
              .flatMap((r) => (Array.isArray(r?.cuisine) ? r.cuisine : []))
              .flatMap((value: any) => {
                if (!value) return [];
                if (typeof value === 'string' || typeof value === 'number') return [String(value)];
                if (typeof value === 'object') return [value?.id, value?.name].map((v: any) => String(v || ''));
                return [String(value)];
              })
              .map((id: any) => String(id || '').trim())
              .filter(Boolean)
          )
        )
          .map((id) => ({ id, name: resolveCuisineName(id) }))
          .sort((a, b) => a.name.localeCompare(b.name));

  const isRestaurantOpenNow = (restaurant: any): boolean => {
    const raw = restaurant?.isOpen ?? restaurant?.is_open;
    if (raw === false || raw === 0 || raw === '0' || String(raw).toLowerCase() === 'false') return false;
    if (raw === true || raw === 1 || raw === '1' || String(raw).toLowerCase() === 'true') return true;
    return true;
  };


  // Calculate comprehensive stats
  const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);
  const activeRestaurants = restaurantsList.filter(r => r.isActive).length;
  const totalOrders = orders.length;
  const avgRating = restaurantsList.length
    ? restaurantsList.reduce((sum, r) => sum + r.rating, 0) / restaurantsList.length
    : 0;
  const totalCustomers = users.filter(u => String(u.role || '').toUpperCase() === 'CUSTOMER').length;
  const activeOrders = orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').length;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayRevenue = orders
    .filter((o) => new Date(o.createdAt).getTime() >= startOfToday.getTime())
    .reduce((sum, order) => sum + order.total, 0);
  const avgOrderValue = totalOrders ? totalRevenue / totalOrders : 0;
  const completionRate = totalOrders
    ? ((orders.filter(o => o.status === 'delivered').length / totalOrders) * 100).toFixed(1)
    : '0.0';

  const revenueTrendData = Array.from({ length: 7 }).map((_, index) => {
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - (6 - index));
    const nextDay = new Date(day);
    nextDay.setDate(nextDay.getDate() + 1);

    const dayOrders = orders.filter((o) => {
      const t = new Date(o.createdAt).getTime();
      return t >= day.getTime() && t < nextDay.getTime();
    });
    const dayRevenue = dayOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
    const dayLabel = day.toLocaleDateString(undefined, { weekday: 'short' });

    return { name: dayLabel, revenue: dayRevenue, orders: dayOrders.length };
  });

  const statusConfig: Array<{ key: string; name: string; color: string }> = [
    { key: 'pending', name: 'Pending', color: '#ef4444' },
    { key: 'confirmed', name: 'Confirmed', color: '#8b5cf6' },
    { key: 'preparing', name: 'Preparing', color: '#3b82f6' },
    { key: 'ready', name: 'Ready', color: '#06b6d4' },
    { key: 'out_for_delivery', name: 'Out for Delivery', color: '#f59e0b' },
    { key: 'delivered', name: 'Delivered', color: '#10b981' },
    { key: 'cancelled', name: 'Cancelled', color: '#6b7280' },
  ];
  const orderStatusData = statusConfig
    .map((status) => ({
      name: status.name,
      value: orders.filter((o) => o.status === status.key).length,
      color: status.color,
    }))
    .filter((row) => row.value > 0);
  const orderStatusChartData =
    orderStatusData.length > 0 ? orderStatusData : [{ name: 'No Orders', value: 1, color: '#d1d5db' }];

  const restaurantPerformanceData = restaurantsList
    .map((r) => {
      const restaurantOrders = orders.filter((o) => String(o.restaurantId || '') === String(r.id));
      return {
        name: String(r.name || '').substring(0, 15),
        orders: restaurantOrders.length,
        revenue: restaurantOrders.reduce((sum, o) => sum + Number(o.total || 0), 0),
        rating: Number(r.rating || 0),
      };
    })
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 8);

  const currentWeekStart = new Date();
  currentWeekStart.setHours(0, 0, 0, 0);
  currentWeekStart.setDate(currentWeekStart.getDate() - 6);
  const previousWeekStart = new Date(currentWeekStart);
  previousWeekStart.setDate(previousWeekStart.getDate() - 7);

  const currentWeekOrders = orders.filter((o) => new Date(o.createdAt) >= currentWeekStart);
  const previousWeekOrders = orders.filter((o) => {
    const created = new Date(o.createdAt);
    return created >= previousWeekStart && created < currentWeekStart;
  });
  const currentWeekRevenue = currentWeekOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
  const previousWeekRevenue = previousWeekOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

  const percentChange = (current: number, previous: number) => {
    if (!previous) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const currentWeekCustomers = new Set(currentWeekOrders.map((o) => String(o.customerId || ''))).size;
  const previousWeekCustomers = new Set(previousWeekOrders.map((o) => String(o.customerId || ''))).size;
  const currentWeekRestaurants = new Set(currentWeekOrders.map((o) => String(o.restaurantId || ''))).size;
  const previousWeekRestaurants = new Set(previousWeekOrders.map((o) => String(o.restaurantId || ''))).size;

  const growthMetrics = {
    revenue: percentChange(currentWeekRevenue, previousWeekRevenue),
    orders: percentChange(currentWeekOrders.length, previousWeekOrders.length),
    customers: percentChange(currentWeekCustomers, previousWeekCustomers),
    restaurants: percentChange(currentWeekRestaurants, previousWeekRestaurants),
  };

  const parseCurrencyFromSettings = (value: string) => {
    if (!value) return { code: '', symbol: '' };
    const codeMatch = value.match(/^([A-Za-z0-9]+)\s*\(/);
    const symbolMatch = value.match(/\(([^)]+)\)/);
    return {
      code: (codeMatch?.[1] || value).trim(),
      symbol: (symbolMatch?.[1] || '').trim(),
    };
  };

  const settingsCurrency = parseCurrencyFromSettings(settings.currency || '');
  const settingsCurrencyOption = currencyOptions.find(
    (option) => option.code === settingsCurrency.code
  );
  const dashboardCurrencyCode =
    settingsCurrencyOption?.code ||
    settingsCurrency.code ||
    '';
  const dashboardCurrencySymbol =
    settingsCurrencyOption?.symbol ||
    settingsCurrency.symbol ||
    dashboardCurrencyCode ||
    '₹';
  const RevenueIcon = dashboardCurrencyCode === 'USD' || dashboardCurrencySymbol === '$'
    ? DollarSign
    : IndianRupee;
  const INR_PER_USD = 83.25;
  const convertDashboardMoney = (value: number) => {
    const safe = Number.isFinite(Number(value)) ? Number(value) : 0;
    if (dashboardCurrencyCode === 'USD') {
      return safe / INR_PER_USD;
    }
    return safe;
  };
  const formatDashboardMoney = (value: number, decimals?: number) => {
    const converted = convertDashboardMoney(value);
    const precision = decimals ?? (dashboardCurrencyCode === 'USD' ? 2 : 0);
    return `${dashboardCurrencySymbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })}`;
  };

  const recentActivityItems: RecentActivity[] = [
    ...orders.map((order, idx) => ({
      id: `order-${order.id || idx}`,
      ts: new Date(order.createdAt).getTime(),
      message: `Order ${order.orderNumber || order.id || ''} moved to ${String(order.status || '').replace(/_/g, ' ')}`,
      icon: ShoppingBag,
      color: 'text-green-600 bg-green-100',
    })),
    ...users
      .filter((u) => u?.createdAt)
      .map((u, idx) => ({
        id: `user-${u.id || idx}`,
        ts: new Date(u.createdAt).getTime(),
        message: `New user registered: ${u.name || u.email || 'User'}`,
        icon: UserCheck,
        color: 'text-purple-600 bg-purple-100',
      })),
    ...restaurantsList
      .filter((r) => r?.updatedAt || r?.createdAt)
      .map((r, idx) => ({
        id: `restaurant-${r.id || idx}`,
        ts: new Date(r.updatedAt || r.createdAt).getTime(),
        message: `Restaurant updated: ${r.name || 'Restaurant'}`,
        icon: Store,
        color: 'text-blue-600 bg-blue-100',
      })),
  ];
  const recentActivities = recentActivityItems
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 5)
    .map((activity) => ({
      ...activity,
      time: formatRelativeTime(activity.ts),
    }));

  const analyticsDaysByRange: Record<AnalyticsRange, number> = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365,
  };
  const analyticsStartDate = new Date();
  analyticsStartDate.setHours(0, 0, 0, 0);
  analyticsStartDate.setDate(analyticsStartDate.getDate() - analyticsDaysByRange[analyticsRange]);

  const analyticsOrders = orders.filter((o) => new Date(o.createdAt) >= analyticsStartDate);
  const restaurantById = new Map(restaurantsList.map((r) => [String(r.id), r]));

  const cityRevenueMap = analyticsOrders.reduce<Map<string, CityAnalytics>>((acc, order) => {
      const restaurant = restaurantById.get(String(order.restaurantId || ''));
      const city = String(restaurant?.city || 'Unknown').trim() || 'Unknown';
      const current = acc.get(city) || { city, revenue: 0, orders: 0 };
      current.orders += 1;
      current.revenue += Number(order.total || 0);
      acc.set(city, current);
      return acc;
    }, new Map<string, CityAnalytics>());

  const topCitiesData = Array.from(cityRevenueMap.values())
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);
  const topCitiesChartData =
    topCitiesData.length > 0 ? topCitiesData : [{ city: 'No Data', revenue: 0, orders: 0 }];

  const newCustomersCount = users.filter((u) => {
    const role = String(u?.role || '').toUpperCase();
    if (role !== 'CUSTOMER') return false;
    if (!u?.createdAt) return false;
    return new Date(u.createdAt) >= analyticsStartDate;
  }).length;

  const customerOrderCounts = analyticsOrders.reduce<Map<string, number>>((acc, order) => {
    const customerId = String(order.customerId || '');
    if (!customerId) return acc;
    acc.set(customerId, (acc.get(customerId) || 0) + 1);
    return acc;
  }, new Map<string, number>());
  const totalOrderingCustomers = customerOrderCounts.size;
  const returningCustomers = Array.from(customerOrderCounts.values()).filter((count) => count > 1).length;
  const returningCustomerRate = totalOrderingCustomers
    ? (returningCustomers / totalOrderingCustomers) * 100
    : 0;

  const deliveredOrders = analyticsOrders.filter((o) => o?.deliveredAt);
  const avgDeliveryMinutes = deliveredOrders.length
    ? deliveredOrders.reduce((sum, o) => {
        const start = new Date(o.placedAt || o.createdAt).getTime();
        const end = new Date(o.deliveredAt).getTime();
        if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return sum;
        return sum + (end - start) / 60000;
      }, 0) / deliveredOrders.length
    : 0;

  const peakHourWindows = [
    { label: '12:00 PM - 2:00 PM', start: 12, end: 14 },
    { label: '7:00 PM - 9:00 PM', start: 19, end: 21 },
    { label: '6:00 PM - 7:00 PM', start: 18, end: 19 },
    { label: '2:00 PM - 4:00 PM', start: 14, end: 16 },
  ];
  const peakHoursData = peakHourWindows.map((slot) => {
    const slotOrders = analyticsOrders.filter((o) => {
      const hour = new Date(o.createdAt).getHours();
      return hour >= slot.start && hour < slot.end;
    }).length;
    return { time: slot.label, orders: slotOrders };
  });
  const peakHourMax = Math.max(1, ...peakHoursData.map((slot) => slot.orders));

  const cuisineOrderCounts = analyticsOrders.reduce<Map<string, number>>((acc, order) => {
    const restaurant = restaurantById.get(String(order.restaurantId || ''));
    const cuisines = Array.isArray(restaurant?.cuisine) ? restaurant.cuisine : [];
    const fallback = cuisines.length ? cuisines : ['Other'];
    for (const cuisine of fallback) {
      const rawCuisine = String(cuisine || 'Other').trim() || 'Other';
      const key = resolveCuisineName(rawCuisine);
      acc.set(key, (acc.get(key) || 0) + 1);
    }
    return acc;
  }, new Map<string, number>());

  const popularCuisineData = Array.from(cuisineOrderCounts.entries())
    .map(([name, orders]) => ({ name, orders }))
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 5);
  const cuisineMax = Math.max(1, ...popularCuisineData.map((c) => c.orders));
  const cuisineColors = ['bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500'];
  const popularCuisinesWithColors = popularCuisineData.map((cuisine, idx) => ({
    ...cuisine,
    color: cuisineColors[idx % cuisineColors.length],
  }));

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800 text-white transition-all duration-300 flex flex-col fixed h-full z-40 shadow-2xl ring-1 ring-white/10`}>
        {/* Logo */}
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-white flex items-center gap-2 text-xl font-semibold tracking-tight">
                  FoodHub
                  <Zap className="w-4 h-4 text-yellow-400 animate-pulse" />
                </h2>
                <p className="text-white/70 text-sm">Super Admin Portal</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'dashboard'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <LayoutDashboard className={`w-5 h-5 flex-shrink-0 ${activeTab === 'dashboard' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && <span>Dashboard</span>}
          </button>
          <button
            onClick={() => setActiveTab('restaurants')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'restaurants'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <Store className={`w-5 h-5 flex-shrink-0 ${activeTab === 'restaurants' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && (
              <div className="flex items-center justify-between w-full">
                <span>Restaurants</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${
                  activeTab === 'restaurants'
                    ? 'bg-white/15 text-white border-white/10'
                    : 'bg-white/10 text-white/80 border-white/10'
                }`}>
                  {restaurantsList.length}
                </span>
              </div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'users'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <Users className={`w-5 h-5 flex-shrink-0 ${activeTab === 'users' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && (
              <div className="flex items-center justify-between w-full">
                <span>Users</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${
                  activeTab === 'users'
                    ? 'bg-white/15 text-white border-white/10'
                    : 'bg-white/10 text-white/80 border-white/10'
                }`}>
                  {usersCountTotal !== null ? usersCountTotal : usersTotal > 0 ? usersTotal : users.length}
                </span>
              </div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'analytics'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <TrendingUp className={`w-5 h-5 flex-shrink-0 ${activeTab === 'analytics' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && <span>Analytics</span>}
          </button>
          <button
            onClick={() => setActiveTab('deliveryApplications')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'deliveryApplications'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <FileText className={`w-5 h-5 flex-shrink-0 ${activeTab === 'deliveryApplications' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && <span>Delivery Applications</span>}
          </button>
          <button
            onClick={() => setActiveTab('masterdata')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'masterdata'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <Database className={`w-5 h-5 flex-shrink-0 ${activeTab === 'masterdata' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && <span>Master Data</span>}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border ${
              activeTab === 'settings'
                ? 'bg-white/15 border-white/10 shadow-sm'
                : 'border-transparent hover:bg-white/10'
            }`}
          >
            <Settings className={`w-5 h-5 flex-shrink-0 ${activeTab === 'settings' ? 'text-white' : 'text-white/80'}`} />
            {sidebarOpen && <span>Settings</span>}
          </button>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border-2 border-white/20" />
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate font-medium">{user.name}</p>
                <p className="text-white/70 text-xs truncate">{user.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6 md:p-8">
          {activeTab === 'dashboard' && (
            <div>
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                  <h1 className="text-gray-900 mb-1">Platform Overview</h1>
                  <p className="text-gray-600">Welcome back, {user.name.split(' ')[0]}! Here's what's happening today.</p>
                </div>
                <div className="flex items-center gap-2">
                  {['today', 'week', 'month', 'year'].map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range as any)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        timeRange === range
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {range.charAt(0).toUpperCase() + range.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <RevenueIcon className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs">
                      <ArrowUp className="w-3 h-3" />
                      {growthMetrics.revenue.toFixed(2)}%
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Total Revenue</h3>
                  <p className="text-3xl font-bold mb-1">{formatDashboardMoney(totalRevenue)}</p>
                  <p className="text-white/60 text-xs">{formatDashboardMoney(todayRevenue)} today</p>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <Store className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs">
                      <ArrowUp className="w-3 h-3" />
                      {growthMetrics.restaurants.toFixed(2)}%
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Active Restaurants</h3>
                  <p className="text-3xl font-bold mb-1">{activeRestaurants}</p>
                  <p className="text-white/60 text-xs">{restaurantsList.length} total restaurants</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs">
                      <ArrowUp className="w-3 h-3" />
                      {growthMetrics.orders.toFixed(2)}%
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Total Orders</h3>
                  <p className="text-3xl font-bold mb-1">{totalOrders}</p>
                  <p className="text-white/60 text-xs">{activeOrders} active now</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs">
                      <ArrowUp className="w-3 h-3" />
                      {growthMetrics.customers.toFixed(2)}%
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Total Customers</h3>
                  <p className="text-3xl font-bold mb-1">{totalCustomers}</p>
                  <p className="text-white/60 text-xs">
                    {usersCountTotal !== null ? usersCountTotal : usersTotal > 0 ? usersTotal : users.length} total users
                  </p>
                </div>
              </div>

              {/* Secondary Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-yellow-500">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <h3 className="text-gray-600 text-sm">Avg Rating</h3>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <span>{avgRating.toFixed(1)}</span>
                    <Star className="w-5 h-5 text-yellow-500" />
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Platform average</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-pink-500">
                  <div className="flex items-center gap-3 mb-2">
                    <Package className="w-5 h-5 text-pink-500" />
                    <h3 className="text-gray-600 text-sm">Avg Order Value</h3>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{formatDashboardMoney(avgOrderValue)}</p>
                  <p className="text-xs text-gray-500 mt-1">Per order</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <h3 className="text-gray-600 text-sm">Completion Rate</h3>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{completionRate}%</p>
                  <p className="text-xs text-gray-500 mt-1">Orders delivered</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-5 h-5 text-blue-500" />
                    <h3 className="text-gray-600 text-sm">Active Now</h3>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{activeOrders}</p>
                  <p className="text-xs text-gray-500 mt-1">Orders in progress</p>
                </div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Revenue Trend */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-gray-900 mb-1">Revenue Trend</h3>
                      <p className="text-gray-600 text-sm">Last 7 days performance</p>
                    </div>
                    <BarChart3 className="w-5 h-5 text-gray-400" />
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={revenueTrendData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip />
                      <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Order Status Distribution */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-gray-900 mb-1">Order Distribution</h3>
                      <p className="text-gray-600 text-sm">By status</p>
                    </div>
                    <PieChart className="w-5 h-5 text-gray-400" />
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={orderStatusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Restaurant Performance */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-gray-900 mb-1">Restaurant Performance</h3>
                    <p className="text-gray-600 text-sm">Top 8 restaurants by orders</p>
                  </div>
                  <Target className="w-5 h-5 text-gray-400" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={restaurantPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={100} />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="orders" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="revenue" fill="#ec4899" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Bottom Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Restaurants */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-gray-900">Top Performing Restaurants</h3>
                    <Award className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div className="space-y-4">
                    {restaurantsList.slice(0, 5).map((restaurant, index) => (
                      <div key={restaurant.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                          {index + 1}
                        </div>
                        <img src={restaurant.logo} alt={restaurant.name} className="w-14 h-14 rounded-lg object-cover shadow-sm" />
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium truncate">{restaurant.name}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <ShoppingBag className="w-3 h-3" />
                              {restaurant.totalOrders} orders
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {restaurant.city}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 mb-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-gray-900 font-bold">{restaurant.rating}</span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            restaurant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {restaurant.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Real-time Activity Feed */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-gray-900">Real-time Activity</h3>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-gray-600">Live</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {recentActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className={`p-2 rounded-lg ${activity.color}`}>
                          <activity.icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 text-sm">{activity.message}</p>
                          <p className="text-gray-500 text-xs mt-1">{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="w-full mt-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm font-medium">
                    View All Activities
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'restaurants' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Restaurants Management</h1>
                  <p className="text-gray-600 mt-1">Manage listed restaurant partners across the city</p>
                </div>
                <button
                  onClick={() => setShowAddRestaurantModal(true)}
                  className="bg-orange-500 text-white px-5 py-2.5 rounded-xl hover:bg-orange-600 transition-colors flex items-center gap-2 shadow-sm w-full md:w-auto justify-center"
                >
                  <Plus className="w-4 h-4" />
                  Add Restaurant
                </button>
              </div>

              {/* Search & Filter */}
              <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-sm p-4 mb-6 border border-white/60">
                <div className="flex flex-col xl:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search restaurants..."
                      value={restaurantSearchTerm}
                      onChange={(e) => setRestaurantSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 xl:w-[520px]">
                    <select
                      value={restaurantCuisineFilter}
                      onChange={(e) => setRestaurantCuisineFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                    >
                      <option value="all">All Categories</option>
                      {restaurantCuisineOptions.map((cuisine) => (
                        <option key={cuisine.id} value={cuisine.id}>{cuisine.name}</option>
                      ))}
                    </select>

                    <select
                      value={restaurantStatusFilter}
                      onChange={(e) => setRestaurantStatusFilter(e.target.value as any)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>

                    <select
                      value={restaurantCityFilter}
                      onChange={(e) => setRestaurantCityFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                    >
                      <option value="all">All Cities</option>
                      {restaurantCityOptions.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {(restaurantStatusFilter !== 'all' || restaurantCityFilter !== 'all' || restaurantCuisineFilter !== 'all') && (
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => {
                          setRestaurantStatusFilter('all');
                          setRestaurantCityFilter('all');
                          setRestaurantCuisineFilter('all');
                        }}
                        className="px-4 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-white border border-gray-200 w-full xl:w-auto"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Restaurants List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {restaurantsLoading && (
                  <div className="col-span-full bg-white rounded-xl shadow-sm p-6 text-gray-600">
                    Loading restaurants...
                  </div>
                )}
                {!restaurantsLoading && filteredRestaurants.length === 0 && (
                  <div className="col-span-full bg-white rounded-xl shadow-sm p-6 text-gray-600">
                    No restaurants found.
                  </div>
                )}
                {!restaurantsLoading && filteredRestaurants.map((restaurant) => (
                  <div
                    key={restaurant.id}
                    className="group bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-xl transition-all border border-gray-100 hover:-translate-y-0.5"
                  >
                    <div className="h-36 bg-gray-200 relative">
                      <ImageWithFallback
                        src={restaurant.banner}
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-white ring-1 ring-black/5 overflow-hidden shrink-0">
                            <ImageWithFallback
                              src={restaurant.logo}
                              alt={restaurant.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="text-gray-900 font-semibold truncate">{restaurant.name}</h3>
                        </div>
                        <span className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium ${
                          !restaurant.isActive
                            ? 'bg-orange-100 text-orange-700'
                            : isRestaurantOpenNow(restaurant)
                              ? 'bg-emerald-600 text-white'
                              : 'bg-red-600 text-white'
                        }`}>
                          {!restaurant.isActive ? 'INACTIVE' : isRestaurantOpenNow(restaurant) ? 'OPEN' : 'OFFLINE'}
                        </span>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="truncate">{restaurant.city || '-'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          {Number(restaurant.rating || 0).toFixed(1)}
                        </div>
                      </div>

                      {(() => {
                        const cuisineIds: string[] = Array.isArray(restaurant.cuisine)
                          ? (restaurant.cuisine as string[])
                          : [];
                        const cuisines = cuisineIds
                          .map((id) => ({ id, name: resolveCuisineName(id) }))
                          .filter((c): c is { id: string; name: string } => Boolean(c.name));
                        const visible = cuisines.slice(0, 2);
                        const remaining = cuisines.length - visible.length;
                        const remainingText = cuisines
                          .slice(2)
                          .map((c) => c.name)
                          .join(', ');

                        if (cuisines.length === 0) return null;

                        return (
                          <div className="mt-3 flex items-center gap-3 text-xs font-medium">
                            {visible.map((c) => (
                              <span key={c.id} className="text-blue-600 hover:text-blue-700 hover:underline cursor-default">
                                {c.name}
                              </span>
                            ))}
                            {remaining > 0 && (
                              <span className="text-gray-500" title={remainingText}>
                                +{remaining}
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => void handleEditRestaurant(restaurant)}
                            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleViewRestaurant(restaurant)}
                            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </div>
                        <button
                          onClick={() => handleDeleteRestaurant(restaurant)}
                          className="p-2 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                          aria-label={`Delete ${restaurant.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-gray-900">User Management</h1>
                <button 
                  onClick={handleOpenAddUserModal}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add User
                </button>
              </div>

              {/* Search Bar */}
              <div className="bg-white/70 backdrop-blur-md rounded-xl shadow-sm p-4 mb-6 border border-white/50">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search users by name, email, role, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white/50 backdrop-blur-sm"
                    />
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="px-4 py-2 text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">User</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Role</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Contact</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Status</th>
                      <th className="px-6 py-3 text-right text-gray-700 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersLoading && (
                      <tr>
                        <td className="px-6 py-4 text-gray-500" colSpan={5}>
                          Loading users...
                        </td>
                      </tr>
                    )}
                    {!usersLoading && filteredUsers.length === 0 && (
                      <tr>
                        <td className="px-6 py-4 text-gray-500" colSpan={5}>
                          No users found.
                        </td>
                      </tr>
                    )}
                    {filteredUsers.map((u) => {
                      return (
                        <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img src={u.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email}`} alt={u.name} className="w-10 h-10 rounded-full" />
                              <div>
                                <p className="text-gray-900 font-medium">{u.name}</p>
                                <p className="text-gray-500 text-sm">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                              {u.role.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-gray-600">{u.phone || '-'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {u.isActive ? '\u25CF Active' : '\u25CF Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleEditUser(u)}
                              disabled={usersActionLoadingId === u.id}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex disabled:opacity-50"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(u)}
                              disabled={usersActionLoadingId === u.id}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors inline-flex disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    {usersTotal
                      ? `Showing ${usersRangeStart}-${usersRangeEnd} of ${usersTotal}`
                      : 'Showing 0 users'}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Rows</span>
                      <select
                        value={usersLimit}
                        onChange={(e) => setUsersLimit(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                        disabled={usersLoading || usersPage <= 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Prev
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {usersPage} of {usersTotalPages}
                      </span>
                      <button
                        onClick={() => setUsersPage((p) => Math.min(usersTotalPages, p + 1))}
                        disabled={usersLoading || usersPage >= usersTotalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div>
              <h1 className="text-gray-900 mb-6">Platform Analytics</h1>
              
              {/* Analytics Header */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-600">Comprehensive analytics and insights for your platform</p>
                <div className="flex items-center gap-2">
                  {(['7d', '30d', '90d', '1y'] as AnalyticsRange[]).map((range) => (
                    <button
                      key={range}
                      onClick={() => setAnalyticsRange(range)}
                      className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                        analyticsRange === range
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Revenue Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Revenue Over Time
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={revenueTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 5 }} />
                      <Line type="monotone" dataKey="orders" stroke="#ec4899" strokeWidth={3} dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Top Performing Cities
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topCitiesChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="city" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Customer Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-8 h-8" />
                    <div>
                      <p className="text-blue-100 text-sm">New Customers</p>
                      <p className="text-3xl font-bold">{newCustomersCount.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <ArrowUp className="w-4 h-4" />
                    <span>Last {analyticsRange}</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <UserCheck className="w-8 h-8" />
                    <div>
                      <p className="text-green-100 text-sm">Returning Customers</p>
                      <p className="text-3xl font-bold">{returningCustomerRate.toFixed(1)}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <ArrowUp className="w-4 h-4" />
                    <span>{returningCustomers} repeat customers</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-8 h-8" />
                    <div>
                      <p className="text-orange-100 text-sm">Avg Delivery Time</p>
                      <p className="text-3xl font-bold">{avgDeliveryMinutes > 0 ? `${Math.round(avgDeliveryMinutes)} min` : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <ArrowDown className="w-4 h-4" />
                    <span>From delivered orders</span>
                  </div>
                </div>
              </div>

              {/* Detailed Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 font-semibold mb-4">Peak Hours Analysis</h3>
                  <div className="space-y-3">
                    {peakHoursData.map((slot) => (
                      <div key={slot.time}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-700 text-sm">{slot.time}</span>
                          <span className="text-gray-900 font-semibold">{slot.orders} orders</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all"
                            style={{ width: `${(slot.orders / peakHourMax) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 font-semibold mb-4">Popular Cuisines</h3>
                  <div className="space-y-3">
                    {popularCuisinesWithColors.map((cuisine) => (
                      <div key={cuisine.name} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${cuisine.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-gray-700 text-sm">{cuisine.name}</span>
                            <span className="text-gray-900 font-semibold text-sm">{cuisine.orders}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`${cuisine.color} h-1.5 rounded-full`}
                              style={{ width: `${(cuisine.orders / cuisineMax) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'masterdata' && (
            <div>
              <h1 className="text-gray-900 mb-6">Master Data Management</h1>
              <p className="text-gray-600 mb-6">
                Configure all master data for the platform. These values will be used across all restaurants and users.
              </p>
              <MasterDataManager />
            </div>
          )}

          {activeTab === 'deliveryApplications' && (
            <div>
              <div className="mb-6">
                <h1 className="text-gray-900 mb-1">Delivery Partner Applications</h1>
                <p className="text-gray-600">Review submitted delivery partner applications and activate approved accounts.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-amber-500">
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {deliveryApplications.filter((item) => item.status === 'PENDING').length}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {deliveryApplications.filter((item) => item.status === 'APPROVED').length}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-red-500">
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {deliveryApplications.filter((item) => item.status === 'REJECTED').length}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={deliveryApplicationsSearch}
                    onChange={(e) => setDeliveryApplicationsSearch(e.target.value)}
                    placeholder="Search by name, email, phone, vehicle number, or area"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <select
                  value={deliveryApplicationsStatus}
                  onChange={(e) => setDeliveryApplicationsStatus(e.target.value as DeliveryApplicationStatus)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="ALL">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Applicant</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Vehicle</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Area</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Bank</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Submitted</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {deliveryApplicationsLoading ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                            Loading applications...
                          </td>
                        </tr>
                      ) : deliveryApplications.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                            No delivery applications found.
                          </td>
                        </tr>
                      ) : (
                        deliveryApplications.map((application) => (
                          <tr key={application.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-gray-900">{application.name}</p>
                                <p className="text-sm text-gray-600">{application.email}</p>
                                <p className="text-xs text-gray-500">{application.phone}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              <p>{application.vehicleType}</p>
                              <p className="text-xs text-gray-500">{application.vehicleNumber || '-'}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{application.deliveryArea || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              <p>{application.bankAccountName || '-'}</p>
                              <p className="text-xs text-gray-500">{application.bankName || '-'}</p>
                              <p className="text-xs text-gray-500">{application.bankIfsc || '-'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  application.status === 'APPROVED'
                                    ? 'bg-green-100 text-green-700'
                                    : application.status === 'REJECTED'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {application.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {application.createdAt ? new Date(application.createdAt).toLocaleString() : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => openDeliveryApplicationModal(application)}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h1 className="text-gray-900 mb-6">Platform Settings</h1>
              
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Settings Navigation */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <nav className="space-y-1">
                    {[
                      { id: 'general' as SettingsSection, icon: Settings, label: 'General Settings' },
                      { id: 'notifications' as SettingsSection, icon: Bell, label: 'Notifications' },
                      { id: 'security' as SettingsSection, icon: Shield, label: 'Security' },
                      { id: 'data_privacy' as SettingsSection, icon: Database, label: 'Data & Privacy' },
                      { id: 'email_templates' as SettingsSection, icon: Mail, label: 'Email Templates' },
                      { id: 'payment_methods' as SettingsSection, icon: CreditCard, label: 'Payment Methods' }
                    ].map((item) => (
                      <button
                        key={item.label}
                        onClick={() => setSettingsSection(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          settingsSection === item.id
                            ? 'bg-purple-50 text-purple-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Settings Content */}
                <div className="lg:col-span-3 space-y-6">
                  {/* General Settings */}
                  {settingsSection === 'general' && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-purple-600" />
                      General Settings
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Platform Name
                        </label>
                        <input
                          type="text"
                          value={settings.platformName}
                          onChange={(e) => {
                            setSettings({ ...settings, platformName: e.target.value });
                            setSettingsDirty(true);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Support Email
                        </label>
                        <input
                          type="email"
                          value={settings.supportEmail}
                          onChange={(e) => {
                            setSettings({ ...settings, supportEmail: e.target.value });
                            setSettingsDirty(true);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Time Zone
                        </label>
                        <select
                          value={settings.timezone}
                          onChange={(e) => {
                            setSettings({ ...settings, timezone: e.target.value });
                            setSettingsDirty(true);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {timezoneOptions.length === 0 && (
                            <option value="">No timezones available</option>
                          )}
                          {timezoneOptions.map((tz) => (
                            <option key={tz.id} value={tz.name}>{tz.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Default Currency
                        </label>
                        <select
                          value={settings.currency}
                          onChange={(e) => {
                            setSettings({ ...settings, currency: e.target.value });
                            setSettingsDirty(true);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {currencyOptions.length === 0 && (
                            <option value="">No currencies available</option>
                          )}
                          {currencyOptions.map((currency) => (
                            <option key={currency.id} value={`${currency.code} (${currency.symbol || currency.code})`}>
                              {currency.code} ({currency.symbol || currency.code})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Notifications Settings */}
                  {settingsSection === 'notifications' && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <h3 className="text-gray-900 font-semibold mb-1 flex items-center gap-2">
                          <Bell className="w-5 h-5 text-purple-600" />
                          Real-Time Notification Center
                        </h3>
                        <p className="text-sm text-gray-600">
                          Live updates every 30 seconds from users and restaurants
                        </p>
                        <div className="mt-2 flex items-center gap-3 text-xs">
                          <span className={`px-2 py-1 rounded-full font-medium ${notificationRefreshing ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                            {notificationRefreshing ? 'Syncing...' : 'Live'}
                          </span>
                          <span className="text-gray-500">
                            Last sync: {lastNotificationSync ? formatRelativeTime(lastNotificationSync) : 'Not synced'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => void refreshRealtimeNotifications()}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${notificationRefreshing ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                      <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                        <p className="text-xs uppercase tracking-wide text-purple-700 font-semibold">Total Alerts</p>
                        <p className="text-2xl font-bold text-purple-900 mt-1">{notificationFeed.length}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                        <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">Unread</p>
                        <p className="text-2xl font-bold text-orange-900 mt-1">
                          {notificationFeed.filter((item) => !item.read).length}
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                        <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">Critical</p>
                        <p className="text-2xl font-bold text-green-900 mt-1">
                          {notificationFeed.filter((item) => item.level === 'warning').length}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-5">
                      {notificationPreferences.map((pref) => (
                        <div key={pref.key} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:border-purple-300 transition-colors">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{pref.label}</p>
                            <p className="text-xs text-gray-500">
                              {pref.checked ? 'Enabled for live alerts' : 'Disabled'}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pref.checked}
                              onChange={(e) => {
                                setNotificationPreferences((prev) =>
                                  prev.map((item) =>
                                    item.key === pref.key ? { ...item, checked: e.target.checked } : item
                                  )
                                );
                                setSettingsDirty(true);
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">Live Alert Feed</h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={markAllNotificationsAsRead}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50"
                        >
                          Mark all read
                        </button>
                        <button
                          onClick={clearReadNotifications}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50"
                        >
                          Clear read
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {notificationFeed.length === 0 && (
                        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600">
                          No alerts yet. Live feed will populate automatically.
                        </div>
                      )}
                      {notificationFeed.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => toggleNotificationRead(item.id)}
                          className={`w-full text-left p-3 rounded-xl border transition-colors ${
                            item.read ? 'bg-white border-gray-200' : 'bg-purple-50 border-purple-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${
                                  item.level === 'warning'
                                    ? 'bg-orange-500'
                                    : item.level === 'success'
                                      ? 'bg-green-500'
                                      : 'bg-blue-500'
                                }`}></span>
                                <p className="font-semibold text-gray-900 text-sm truncate">{item.title}</p>
                                {!item.read && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-600 text-white">
                                    New
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-700">{item.message}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {item.source} {String.fromCharCode(0x2022)} {formatRelativeTime(item.createdAt)}
                              </p>
                            </div>
                            <div className="pt-0.5">
                              {item.read ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-purple-600" />
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Security Settings */}
                  {settingsSection === 'security' && (
                  <>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      Security & Access
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-gray-900 font-medium">Two-Factor Authentication</p>
                          <p className="text-gray-600 text-sm">Add an extra layer of security</p>
                        </div>
                        <button
                          onClick={() => {
                            setTwoFactorEnabled((prev) => !prev);
                            setSettingsDirty(true);
                          }}
                          className={`px-4 py-2 rounded-lg transition-colors text-sm text-white ${
                            twoFactorEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          {twoFactorEnabled ? 'Enabled' : 'Enable'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-gray-900 font-medium">Session Timeout</p>
                          <p className="text-gray-600 text-sm">Auto logout after {sessionTimeout}</p>
                        </div>
                        <select
                          value={sessionTimeout}
                          onChange={(e) => {
                            setSessionTimeout(e.target.value);
                            setSettingsDirty(true);
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        >
                          <option>15 minutes</option>
                          <option>30 minutes</option>
                          <option>1 hour</option>
                          <option>Never</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-gray-900 font-medium">Login History</p>
                          <p className="text-gray-600 text-sm">View recent login activity</p>
                        </div>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm">
                          View Logs
                        </button>
                      </div>
                    </div>
                  </div>

                  </>
                  )}

                  {settingsSection === 'data_privacy' && (
                    <>
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-5">
                          <div>
                            <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                              <Database className="w-5 h-5 text-purple-600" />
                              Data & Privacy Center
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Live governance metrics and privacy controls
                            </p>
                          </div>
                          <button
                            onClick={() => void refreshAdvancedSettingsData()}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                          <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                            <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">User Records</p>
                            <p className="text-2xl font-bold text-blue-900 mt-1">
                              {usersCountTotal !== null ? usersCountTotal : usersTotal > 0 ? usersTotal : users.length}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                            <p className="text-xs uppercase tracking-wide text-purple-700 font-semibold">Restaurant Records</p>
                            <p className="text-2xl font-bold text-purple-900 mt-1">{restaurantsList.length}</p>
                          </div>
                          <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                            <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">Event Logs</p>
                            <p className="text-2xl font-bold text-green-900 mt-1">{notificationFeed.length}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {privacyControls.map((control) => (
                            <div key={control.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                              <div>
                                <p className="font-semibold text-gray-900">{control.label}</p>
                                <p className="text-xs text-gray-500 mt-1">{control.description}</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={control.enabled}
                                  onChange={() => togglePrivacyControl(control.key)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Security Event Stream</h4>
                        <div className="space-y-2">
                          {securityEvents.map((event) => (
                            <div key={event.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${event.status === 'warn' ? 'bg-orange-500' : 'bg-green-500'}`}></span>
                                <span className="text-sm text-gray-800">{event.title}</span>
                              </div>
                              <span className="text-xs text-gray-500">{formatRelativeTime(event.createdAt)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {settingsSection === 'email_templates' && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                            <Mail className="w-5 h-5 text-purple-600" />
                            Email Template Orchestration
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Dynamic template health, delivery counts, and activation
                          </p>
                        </div>
                        <span className="text-xs text-gray-500">
                          Synced {lastRealtimeSettingsSync ? formatRelativeTime(lastRealtimeSettingsSync) : 'Not synced'}
                        </span>
                      </div>

                      <div className="space-y-3">
                        {emailTemplates.map((template) => (
                          <div key={template.key} className="p-4 rounded-xl border border-gray-200 hover:border-purple-300 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-gray-900">{template.name}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {template.channel.toUpperCase()} {String.fromCharCode(0x2022)} {template.deliveries} deliveries {String.fromCharCode(0x2022)} Last run {formatRelativeTime(template.lastTriggeredAt)}
                                </p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={template.enabled}
                                  onChange={() => toggleEmailTemplate(template.key)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {settingsSection === 'payment_methods' && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-purple-600" />
                            Payment Methods Control Plane
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Real-time usage and activation controls from master data
                          </p>
                        </div>
                        <button
                          onClick={() => void refreshAdvancedSettingsData()}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                        <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                          <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">Total Methods</p>
                          <p className="text-2xl font-bold text-indigo-900 mt-1">{paymentMethodsConfig.length}</p>
                        </div>
                        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                          <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Active</p>
                          <p className="text-2xl font-bold text-emerald-900 mt-1">
                            {paymentMethodsConfig.filter((m) => m.isActive).length}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                          <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Total Transactions</p>
                          <p className="text-2xl font-bold text-amber-900 mt-1">
                            {paymentMethodsConfig.reduce((acc, item) => acc + item.usageCount, 0)}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {paymentMethodsConfig.map((method) => (
                          <div key={method.id} className="p-4 rounded-xl border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-gray-900">{method.name}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {method.code || 'N/A'} {String.fromCharCode(0x2022)} {method.usageCount} uses {String.fromCharCode(0x2022)} {method.lastUsedAt ? `Last used ${formatRelativeTime(method.lastUsedAt)}` : 'No usage yet'}
                                </p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={method.isActive}
                                  onChange={() => togglePaymentMethodConfig(method.id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                              </label>
                            </div>
                          </div>
                        ))}
                        {paymentMethodsConfig.length === 0 && (
                          <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600">
                            No payment methods found from master data.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={loadSavedSettings}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSettingsToStorage}
                      disabled={!settingsDirty}
                      className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                        settingsDirty ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
          setUserModalMode('add');
        }}
        onSuccess={handleUserSaved}
        restaurants={restaurantsList}
        mode={userModalMode}
        initialData={selectedUser}
      />

      {/* Add Restaurant Modal */}
      <AddRestaurantModal
        isOpen={showAddRestaurantModal}
        onClose={() => setShowAddRestaurantModal(false)}
        onSuccess={handleRestaurantAdded}
      />

      <AddRestaurantModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRestaurant(null);
        }}
        onSuccess={(message) => {
          if (message) success(message);
          void loadRestaurants();
        }}
        mode="edit"
        initialData={selectedRestaurant}
        onSubmit={handleRestaurantUpdate}
      />

      {/* View Restaurant Modal */}
      {showViewModal && selectedRestaurant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Restaurant Details</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <ImageWithFallback
                  src={selectedRestaurant.image}
                  alt={selectedRestaurant.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Restaurant Name</label>
                  <p className="text-gray-900 mt-1">{selectedRestaurant.name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Description</label>
                  <p className="text-gray-900 mt-1">{selectedRestaurant.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Rating</label>
                    <p className="text-gray-900 mt-1 flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {selectedRestaurant.rating}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Delivery Time</label>
                    <p className="text-gray-900 mt-1">{selectedRestaurant.deliveryTime}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Location</label>
                  <p className="text-gray-900 mt-1">{selectedRestaurant.address}, {selectedRestaurant.city}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Cuisine Types</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedRestaurant.cuisine?.length === 0 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        No cuisine tags
                      </span>
                    )}
                    {selectedRestaurant.cuisine?.map((c: string) => (
                      <span key={c} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {resolveCuisineName(c)}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Status</label>
                  <p className={`mt-1 inline-flex items-center px-3 py-1 rounded-full text-sm ${
                    selectedRestaurant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedRestaurant.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeliveryApplicationModal && selectedDeliveryApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5 flex items-start justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Delivery Application Review</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedDeliveryApplication.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowDeliveryApplicationModal(false);
                  setSelectedDeliveryApplication(null);
                  setDeliveryReviewNotes('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 bg-gray-50">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  selectedDeliveryApplication.status === 'APPROVED'
                    ? 'bg-green-100 text-green-700'
                    : selectedDeliveryApplication.status === 'REJECTED'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                }`}>
                  {selectedDeliveryApplication.status}
                </span>
                <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  Submitted {selectedDeliveryApplication.createdAt ? new Date(selectedDeliveryApplication.createdAt).toLocaleString() : '-'}
                </span>
                {selectedDeliveryApplication.activatedAt && (
                  <span className="inline-flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    Activated {new Date(selectedDeliveryApplication.activatedAt).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Applicant</h3>
                  <div className="space-y-3 text-sm">
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Name</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.name}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Email</span><span className="text-gray-900 font-medium text-right break-all">{selectedDeliveryApplication.email}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Phone</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.phone}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Linked User</span><span className="text-gray-900 font-medium text-right break-all">{selectedDeliveryApplication.accountUserId || '-'}</span></p>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Application Status</h3>
                  <div className="space-y-3 text-sm">
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Status</span><span className="text-gray-900 font-medium">{selectedDeliveryApplication.status}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Submitted</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.createdAt ? new Date(selectedDeliveryApplication.createdAt).toLocaleString() : '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Reviewed</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.reviewedAt ? new Date(selectedDeliveryApplication.reviewedAt).toLocaleString() : '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Activated</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.activatedAt ? new Date(selectedDeliveryApplication.activatedAt).toLocaleString() : '-'}</span></p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Vehicle & KYC</h3>
                  <div className="space-y-3 text-sm">
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Vehicle Type</span><span className="text-gray-900 font-medium">{selectedDeliveryApplication.vehicleType}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Vehicle Number</span><span className="text-gray-900 font-medium">{selectedDeliveryApplication.vehicleNumber || '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Delivery Area</span><span className="text-gray-900 font-medium">{selectedDeliveryApplication.deliveryArea || '-'}</span></p>
                    <div className="pt-3 flex flex-wrap gap-2">
                      {selectedDeliveryApplication.idProofUrl && (
                        <a
                          href={resolveAssetUrl(selectedDeliveryApplication.idProofUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-xl hover:bg-blue-100"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Aadhaar / ID Proof
                        </a>
                      )}
                      {selectedDeliveryApplication.drivingLicenseUrl && (
                        <a
                          href={resolveAssetUrl(selectedDeliveryApplication.drivingLicenseUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-100"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Driving License
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Bank Details</h3>
                  <div className="space-y-3 text-sm">
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Account Name</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.bankAccountName || '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Bank Name</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.bankName || '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Branch Name</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.branchName || '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">Account Number</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.bankAccountNumber || '-'}</span></p>
                    <p className="flex items-start justify-between gap-3"><span className="text-gray-500">IFSC</span><span className="text-gray-900 font-medium text-right">{selectedDeliveryApplication.bankIfsc || '-'}</span></p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Review Notes</label>
                <textarea
                  value={deliveryReviewNotes}
                  onChange={(e) => setDeliveryReviewNotes(e.target.value)}
                  className="w-full min-h-[120px] rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Add review notes for the delivery partner"
                />
              </div>

              {selectedDeliveryApplication.status === 'PENDING' && (
                <div className="sticky bottom-0 bg-gray-50 pt-2">
                  <div className="flex justify-end gap-3">
                  <button
                    onClick={() => void handleDeliveryApplicationReview('REJECTED')}
                    disabled={deliveryReviewActionLoading}
                    className="px-5 py-2.5 rounded-xl bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-60 font-medium"
                  >
                    {deliveryReviewActionLoading ? 'Please wait...' : 'Reject'}
                  </button>
                  <button
                    onClick={() => void handleDeliveryApplicationReview('APPROVED')}
                    disabled={deliveryReviewActionLoading}
                    className="px-5 py-2.5 rounded-xl bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-60 font-medium"
                  >
                    {deliveryReviewActionLoading ? 'Please wait...' : 'Approve'}
                  </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedRestaurant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Delete Restaurant</h3>
                <p className="text-gray-600 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{selectedRestaurant.name}</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteRestaurant}
                disabled={restaurantsActionLoading}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                {restaurantsActionLoading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedRestaurant(null);
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

